import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { getAllProjects } from '../modules/builder/services/api/implementation/impl';

// Create the context
const ProjectsContext = createContext();

// Custom hook to use the projects context
export const useProjectsList = () => {
  const context = useContext(ProjectsContext);
  if (!context) {
    throw new Error('useProjectsList must be used within a ProjectsProvider');
  }
  return context;
};

// Projects Provider Component
export const ProjectsProvider = ({ children }) => {
  // State to track authentication status
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Fetch projects using React Query - manually controlled
  const {
    data: projects = [],
    refetch: refetchProjects,
    isFetching: isProjectsLoading,
    error: projectsError,
    isSuccess: isProjectsSuccess
  } = useQuery({
    queryKey: ['allProjects'],
    queryFn: getAllProjects,
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes - cache persists for 10 minutes
    refetchOnWindowFocus: false, // Don't refetch when window gains focus
    enabled: false // Never auto-fetch, only manual refetch
  });

  // Transform projects for dropdown use
  const projectsForDropdown =
    projects?.map((project) => ({
      label: project.name || '',
      id: project.id,
      value: project.id
    })) || [];

  // Manual trigger for refetching projects (useful after login)
  const triggerProjectsRefresh = () => {
    console.log('Manually triggering projects refresh');
    refetchProjects();
  };

  // Check authentication status on mount and when cookies change
  useEffect(() => {
    const checkAuthStatus = () => {
      const hasToken =
        !!Cookies.get('token') || !!Cookies.get('accessToken') || !!Cookies.get('userId');
      setIsLoggedIn(hasToken);
    };

    // Check initially
    checkAuthStatus();

    // If user is already logged in on mount, fetch projects
    const hasToken =
      !!Cookies.get('token') || !!Cookies.get('accessToken') || !!Cookies.get('userId');
    if (hasToken) {
      console.log('User already logged in on mount - fetching projects');
      refetchProjects();
    }

    // Set up an interval to periodically check auth status
    // This helps catch logout events or token expiration
    const interval = setInterval(checkAuthStatus, 5000); // Check every 5 seconds for logout detection

    return () => clearInterval(interval);
  }, [refetchProjects]);

  // Listen for custom login events - this is the main trigger for refetching
  useEffect(() => {
    const handleLoginEvent = () => {
      console.log('Login event detected - refreshing projects');
      setTimeout(() => {
        refetchProjects();
      }, 1000); // Small delay to ensure cookies are set
    };

    // Listen for custom login event
    window.addEventListener('userLoggedIn', handleLoginEvent);

    return () => {
      window.removeEventListener('userLoggedIn', handleLoginEvent);
    };
  }, [refetchProjects]);

  // Utility functions
  const getProjectById = (id) => {
    return projects?.find((p) => p.id === id) || null;
  };

  const getProjectByName = (name) => {
    return projects?.find((p) => p.name?.toLowerCase() === name?.toLowerCase()) || null;
  };

  const value = {
    // Data
    projects,
    projectsForDropdown,

    // Status
    isProjectsLoading,
    isProjectsSuccess,
    projectsError,
    isLoggedIn,
    isReady: projects?.length > 0 && !isProjectsLoading && isLoggedIn,

    // Actions
    refetchProjects,
    triggerProjectsRefresh,

    // Utilities
    getProjectById,
    getProjectByName
  };

  return <ProjectsContext.Provider value={value}>{children}</ProjectsContext.Provider>;
};

export default ProjectsContext;
